import React, {
	Dispatch,
	RefAttributes,
	memo,
	useCallback,
	useMemo,
	useState,
} from "react";
import {
	CheckBox,
	Stepper,
	TextBox,
	ToggleButton,
	react,
	useRefWithSetter,
} from "uikit";
import { FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useObjectEditor from "../../../../../../hooks/useObjectEditor";
import PaymentSystem, {
	PaymentSystemFrontEndType,
} from "../../../../../../services/PaymentSystem";
import {
	StyledColumn,
	StyledRow,
	StyledP,
} from "../../../../../../components/common";
import translationPath from "../../constants/translationPath";
import ValueStepper from "../ValueStepper";

import InternalController from "./Controller";
import MonobankPaymentsCurrency from "./components/MonobankPaymentsCurrency";
import MonobankPaymentsCountryIso from "./components/MonobankPaymentsCountryIso";

const MonobankPaymentBase = react.withController<
	MonobankPayment.PropsBase,
	MonobankPayment.Controller
>(({ value, onChange, controller }) => {
	const { t } = useTranslation();

	const [countryIsoRef, setCountryIsoRef] =
		useRefWithSetter<MonobankPaymentsCountryIso.Ref | null>(null);
	const [currencyRef, setCurrencyRef] =
		useRefWithSetter<MonobankPaymentsCurrency.Ref | null>(null);

	const valueEditor = useObjectEditor(value, onChange);

	const type = valueEditor.useGetter("type");

	const isExecutor = useMemo(
		() =>
			PaymentSystemFrontEndType.EXECUTOR === type ||
			PaymentSystemFrontEndType.TEMPLATE_EXECUTOR === type,
		[type],
	);

	const isCustomer = useMemo(
		() =>
			PaymentSystemFrontEndType.CUSTOMER === type ||
			PaymentSystemFrontEndType.TEMPLATE_CUSTOMER === type,
		[type],
	);

	// monobankToken
	const monobankToken = valueEditor.useGetter("monobankToken");
	const setMonobankToken = valueEditor.useSetter("monobankToken");
	const [errorMonobankToken, setMonobankTokenError] =
		useState<boolean>(false);
	const handleMonobankToken = useCallback(
		(value: string) => {
			if (typeof value === "string") {
				setMonobankToken(value);
				setMonobankTokenError(false);
			}
		},
		[setMonobankToken, setMonobankTokenError],
	);

	const textErrorMonobankToken = useMemo(() => {
		if (!errorMonobankToken) return "";
		return t([
			`${translationPath}.modal.errors.monobankToken`,
			"Merchant token not entered",
		]);
	}, [t, errorMonobankToken]);

	const placeholderMonobankToken = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.monobankToken`,
				"Enter merchant token",
			]),
		[t],
	);

	const textMonobankToken = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.monobankToken`,
				"Merchant token (X-Token)",
			]),
		[t],
	);

	const textMonobankTokenHint = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.monobankTokenHint`,
				"Production token — monobank business cabinet. Test token — api.monobank.ua (merchantId starts with test_, no real charges).",
			]),
		[t],
	);

	// monobankApiUrl
	const monobankApiUrl = valueEditor.useGetter("monobankApiUrl");
	const setMonobankApiUrl = valueEditor.useSetter("monobankApiUrl");
	const [errorMonobankApiUrl, setMonobankApiUrlError] =
		useState<boolean>(false);
	const handleMonobankApiUrl = useCallback(
		(value: string) => {
			if (typeof value === "string") {
				setMonobankApiUrl(value);
				setMonobankApiUrlError(false);
			}
		},
		[setMonobankApiUrl],
	);

	const textErrorMonobankApiUrl = useMemo(() => {
		if (!errorMonobankApiUrl) return "";
		return t([
			`${translationPath}.modal.errors.monobankApiUrl`,
			"API URL not entered",
		]);
	}, [t, errorMonobankApiUrl]);

	const placeholderMonobankApiUrl = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.monobankApiUrl`,
				"e.g. https://api.monobank.ua",
			]),
		[t],
	);

	const textMonobankApiUrl = useMemo(
		() => t([`${translationPath}.modal.text.monobankApiUrl`, "API URL"]),
		[t],
	);

	// monobankFeePercentage
	const monobankFeePercentageValue = valueEditor.useGetter(
		"monobankFeePercentage",
	);
	const setMonobankFeePercentageValue = valueEditor.useSetter(
		"monobankFeePercentage",
	);
	const handleMonobankFeePercentage = useCallback(
		(value: Stepper.Value) => {
			if (typeof value === "number") {
				setMonobankFeePercentageValue(value);
			}
		},
		[setMonobankFeePercentageValue],
	);

	const textMonobankFeePercentage = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.monobankFeePercentage`,
				"monobank commission in %",
			]),
		[t],
	);

	// monobankAdditionalFee
	const monobankAdditionalFeeValue = valueEditor.useGetter(
		"monobankAdditionalFee",
	);
	const setMonobankAdditionalFeeValue = valueEditor.useSetter(
		"monobankAdditionalFee",
	);
	const handleMonobankAdditionalFee = useCallback(
		(value: Stepper.Value) => {
			if (typeof value === "number") {
				setMonobankAdditionalFeeValue(value);
			}
		},
		[setMonobankAdditionalFeeValue],
	);

	const textMonobankAdditionalFee = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.monobankAdditionalFee`,
				"monobank commission in UAH",
			]),
		[t],
	);

	const textMonobankFeeEstimateHint = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.monobankFeeEstimateHint`,
				"Used for the card hold/capture amount when the commission is charged to the client.",
			]),
		[t],
	);

	// systemFeePercentageForExecutor
	const systemFeePercentageForExecutorValue = valueEditor.useGetter(
		"systemFeePercentageForExecutor",
	);
	const setSystemFeePercentageForExecutorValue = valueEditor.useSetter(
		"systemFeePercentageForExecutor",
	);
	const handleSystemFeePercentageForExecutor = useCallback(
		(value: Stepper.Value) => {
			if (typeof value === "number") {
				setSystemFeePercentageForExecutorValue(value);
			}
		},
		[setSystemFeePercentageForExecutorValue],
	);

	const textSystemFeePercentageForExecutor = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.systemFeePercentageForExecutor`,
				"Additional driver commission in %",
			]),
		[t],
	);

	// systemFeeAdditionalForExecutor
	const systemFeeAdditionalForExecutorValue = valueEditor.useGetter(
		"systemFeeAdditionalForExecutor",
	);
	const setSystemFeeAdditionalForExecutorValue = valueEditor.useSetter(
		"systemFeeAdditionalForExecutor",
	);
	const handleSystemFeeAdditionalForExecutor = useCallback(
		(value: Stepper.Value) => {
			if (typeof value === "number") {
				setSystemFeeAdditionalForExecutorValue(value);
			}
		},
		[setSystemFeeAdditionalForExecutorValue],
	);

	const textSystemFeeAdditionalForExecutor = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.systemFeeAdditionalForExecutor`,
				"Additional commission from the driver in UAH.",
			]),
		[t],
	);

	// monobankGlobalFeeFromSystem
	const monobankGlobalFeeFromSystemValue = valueEditor.useGetter(
		"monobankGlobalFeeFromSystem",
	);
	const setMonobankGlobalFeeFromSystemValue = valueEditor.useSetter(
		"monobankGlobalFeeFromSystem",
	);

	const showMonobankFeeEstimateWarning = useMemo(
		() =>
			!monobankGlobalFeeFromSystemValue &&
			!monobankFeePercentageValue &&
			!monobankAdditionalFeeValue,
		[
			monobankGlobalFeeFromSystemValue,
			monobankFeePercentageValue,
			monobankAdditionalFeeValue,
		],
	);

	// systemReturnFeeToExecutor
	const systemReturnFeeToExecutorValue = valueEditor.useGetter(
		"systemReturnFeeToExecutor",
	);
	const systemReturnFeeToExecutorOnChange = valueEditor.useSetter(
		"systemReturnFeeToExecutor",
	);

	const textGlobalFee = useMemo(() => {
		const fromClient = isCustomer
			? t([
					`${translationPath}.modal.text.client`,
					"Commission from the client",
			  ])
			: t([
					`${translationPath}.modal.text.client_executor`,
					"Commission from the client",
			  ]);

		const fromTaxiService =
			t(`${translationPath}.modal.text.commissionFromServiseTaxi`) ?? "";

		return monobankGlobalFeeFromSystemValue ? fromTaxiService : fromClient;
	}, [monobankGlobalFeeFromSystemValue, isCustomer, t]);

	const placeholderFeeToExecutor = useMemo(
		() =>
			t([
				`${translationPath}.modal.label.feeToExecutor`,
				"Driver compensation",
			]),
		[t],
	);

	// monobankAllowGooglePay
	const monobankAllowGooglePayValue = valueEditor.useGetter(
		"monobankAllowGooglePay",
	);
	const setMonobankAllowGooglePayValue = valueEditor.useSetter(
		"monobankAllowGooglePay",
	);
	const textAllowGooglePay = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.monobankAllowGooglePay`,
				"Allow Google Pay payments",
			]),
		[t],
	);

	// monobankAllowApplePay
	const monobankAllowApplePayValue = valueEditor.useGetter(
		"monobankAllowApplePay",
	);
	const setMonobankAllowApplePayValue = valueEditor.useSetter(
		"monobankAllowApplePay",
	);
	const textAllowApplePay = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.monobankAllowApplePay`,
				"Allow Apple Pay payments",
			]),
		[t],
	);

	// monobankAllowQrPayment
	const monobankAllowQrPaymentValue = valueEditor.useGetter(
		"monobankAllowQrPayment",
	);
	const setMonobankAllowQrPaymentValue = valueEditor.useSetter(
		"monobankAllowQrPayment",
	);
	const textAllowQrPayment = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.monobankAllowQrPayment`,
				"Allow payment by QR code",
			]),
		[t],
	);

	// monobankAllowSmsPaymentLink
	const monobankAllowSmsPaymentLinkValue = valueEditor.useGetter(
		"monobankAllowSmsPaymentLink",
	);
	const setMonobankAllowSmsPaymentLinkValue = valueEditor.useSetter(
		"monobankAllowSmsPaymentLink",
	);
	const textAllowSmsPaymentLink = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.monobankAllowSmsPaymentLink`,
				"Allow sending payment link by SMS",
			]),
		[t],
	);

	const textWalletProductionHint = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.monobankWalletProductionHint`,
				"Apple Pay / Google Pay buttons are not shown with a test token — a production merchant is required.",
			]),
		[t],
	);

	// Currency and CountryIso
	const textCurrency = useMemo(
		() => t([`${translationPath}.modal.text.currency`, "Currency"]),
		[t],
	);
	const textCountryIso = useMemo(
		() => t([`${translationPath}.modal.text.countryIso`, "Country"]),
		[t],
	);

	// controller
	controller.setContext({
		monobankToken,
		monobankApiUrl,
		currencyRef,
		countryIsoRef,
		setMonobankTokenError,
		setMonobankApiUrlError,
	});

	return (
		<StyledColumn gap="10px" w="100%">
			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textMonobankToken} :</StyledP>
				<TextBox.TextBox
					style={{ width: "calc(100% - 200px)" }}
					placeholder={placeholderMonobankToken}
					error={textErrorMonobankToken}
					value={monobankToken}
					onChange={handleMonobankToken}
				/>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px" />
				<StyledP style={{ fontSize: "12px", opacity: 0.85 }}>
					{textMonobankTokenHint}
				</StyledP>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textMonobankApiUrl} :</StyledP>
				<TextBox.TextBox
					style={{ width: "calc(100% - 200px)" }}
					placeholder={placeholderMonobankApiUrl}
					error={textErrorMonobankApiUrl}
					value={monobankApiUrl}
					onChange={handleMonobankApiUrl}
				/>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textMonobankFeePercentage}:</StyledP>
				<ValueStepper
					width="80px"
					min={0}
					value={monobankFeePercentageValue}
					onChange={handleMonobankFeePercentage}
				/>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textMonobankAdditionalFee}:</StyledP>
				<ValueStepper
					width="80px"
					min={0}
					value={monobankAdditionalFeeValue}
					onChange={handleMonobankAdditionalFee}
				/>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px" />
				<StyledP style={{ fontSize: "12px", opacity: 0.85 }}>
					{textMonobankFeeEstimateHint}
				</StyledP>
			</StyledRow>

			{showMonobankFeeEstimateWarning && (
				<StyledRow alignItems="center" gap="10px" w="100%">
					<StyledP w="200px" />
					<StyledP style={{ fontSize: "12px", color: "#c77d00" }}>
						{t([
							`${translationPath}.modal.text.monobankFeeEstimateWarning`,
							"Commission is charged to the client, but the estimated fee is 0 — set % or a fixed amount for hold/capture.",
						])}
					</StyledP>
				</StyledRow>
			)}

			{isExecutor && (
				<>
					<StyledRow alignItems="center" gap="10px" w="100%">
						<StyledP w="200px">
							{textSystemFeePercentageForExecutor}:
						</StyledP>
						<ValueStepper
							width="80px"
							min={0}
							value={systemFeePercentageForExecutorValue}
							onChange={handleSystemFeePercentageForExecutor}
						/>
					</StyledRow>

					<StyledRow alignItems="center" gap="10px" w="100%">
						<StyledP w="200px">
							{textSystemFeeAdditionalForExecutor}:
						</StyledP>
						<ValueStepper
							width="80px"
							min={0}
							value={systemFeeAdditionalForExecutorValue}
							onChange={handleSystemFeeAdditionalForExecutor}
						/>
					</StyledRow>
				</>
			)}

			<StyledRow alignItems="center" gap="10px">
				<StyledRow
					alignItems="center"
					gap="10px"
					onClick={(event) => event.stopPropagation()}
				>
					<ToggleButton.ToggleButton
						value={monobankGlobalFeeFromSystemValue}
						onChange={setMonobankGlobalFeeFromSystemValue}
					/>
					<StyledP>{textGlobalFee}</StyledP>
				</StyledRow>
				<StyledRow alignItems="center" gap="10px" m="0 0 0 1rem">
					<CheckBox
						disabled={!monobankGlobalFeeFromSystemValue}
						label={placeholderFeeToExecutor}
						value={systemReturnFeeToExecutorValue}
						onChange={systemReturnFeeToExecutorOnChange}
					/>
				</StyledRow>
			</StyledRow>

			<StyledRow
				alignItems="center"
				gap="10px"
				onClick={(event) => event.stopPropagation()}
			>
				<ToggleButton.ToggleButton
					value={monobankAllowGooglePayValue}
					onChange={setMonobankAllowGooglePayValue}
				/>
				<StyledP>{textAllowGooglePay}</StyledP>
			</StyledRow>

			<StyledRow
				alignItems="center"
				gap="10px"
				onClick={(event) => event.stopPropagation()}
			>
				<ToggleButton.ToggleButton
					value={monobankAllowApplePayValue}
					onChange={setMonobankAllowApplePayValue}
				/>
				<StyledP>{textAllowApplePay}</StyledP>
			</StyledRow>

			<StyledRow
				alignItems="center"
				gap="10px"
				onClick={(event) => event.stopPropagation()}
			>
				<ToggleButton.ToggleButton
					value={monobankAllowQrPaymentValue}
					onChange={setMonobankAllowQrPaymentValue}
				/>
				<StyledP>{textAllowQrPayment}</StyledP>
			</StyledRow>

			<StyledRow
				alignItems="center"
				gap="10px"
				onClick={(event) => event.stopPropagation()}
			>
				<ToggleButton.ToggleButton
					value={monobankAllowSmsPaymentLinkValue}
					onChange={setMonobankAllowSmsPaymentLinkValue}
				/>
				<StyledP>{textAllowSmsPaymentLink}</StyledP>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px" />
				<StyledP style={{ fontSize: "12px", opacity: 0.85 }}>
					{textWalletProductionHint}
				</StyledP>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px">
				<StyledP w="200px">{textCurrency}:</StyledP>
				<MonobankPaymentsCurrency
					ref={setCurrencyRef}
					value={value}
					onChange={onChange}
				/>
			</StyledRow>
			<StyledRow alignItems="center" gap="10px">
				<StyledP w="200px">{textCountryIso}:</StyledP>
				<MonobankPaymentsCountryIso
					ref={setCountryIsoRef}
					value={value}
					onChange={onChange}
				/>
			</StyledRow>
		</StyledColumn>
	);
}, InternalController);

const MonobankPayment = memo(MonobankPaymentBase);

declare namespace MonobankPayment {
	type Ref = InternalController | null;
	type Controller = InternalController;
	interface PropsBase {
		value: Value;
		onChange: Dispatch<Value>;
		errors?: FieldErrors<any>;
	}
	type Props = PropsBase & RefAttributes<Ref>;

	interface Value extends PaymentSystem.Model {}
}

export default MonobankPayment;
